// Cache hydration. Given a new manifest, downloads every referenced blob
// that isn't already cached, verifies size against the snapshot, and stores
// it under its sha256. Only marks the manifest as "last good" if every item
// succeeds — partial failures keep the prior manifest in place.
//
// This is the gate that enforces the kiosk invariant: never enter PLAYING
// with a manifest unless all assets are on disk. If hydration fails, the
// previous lastGoodManifest stays put and current playback continues.
//
// M2E.C: hydrate downloads BOTH MEDIA playlist items AND any additional
// media referenced by non-MEDIA items via the manifest.mediaToPrecache
// list (brand logos, menu backgrounds, item images). Targets are deduped
// by sha256, so the same blob is never fetched twice in one pass.

import { hasBlob, putBlob, setMeta, getMeta } from './cache';
import { setLastManifestHash } from './storage';
import { ASSET_FETCH_CONCURRENCY } from './config';
import type { Manifest, ManifestMediaItem } from './types';

const LAST_GOOD_KEY = 'lastGoodManifest';

export interface HydrationProgress {
  totalItems: number;
  itemsCompleted: number;
  itemsDownloaded: number;
  itemsFailed: number;
}

export interface HydrationError {
  sha256: string;
  message: string;
}

export interface HydrationResult {
  ok: boolean;
  totalItems: number;
  cachedBefore: number;
  downloaded: number;
  failed: number;
  errors: HydrationError[];
}

export interface HydrateOptions {
  onProgress?: (p: HydrationProgress) => void;
}

interface DownloadTarget {
  sha256: string;
  url: string;
  sizeBytes: string;
}

export async function getLastGoodManifest(): Promise<Manifest | null> {
  return getMeta<Manifest>(LAST_GOOD_KEY);
}

// Flatten the manifest into a deduped list of blobs to download.
// MEDIA items from playlist.items contribute their inline (url, sha256).
// MediaRefs from manifest.mediaToPrecache contribute any extra assets
// referenced by MENU_BOARD/PROMO items. Dedup is by sha256 — the same
// underlying file (same sha256) is fetched at most once.
function buildDownloadTargets(manifest: Manifest): DownloadTarget[] {
  const targets: DownloadTarget[] = [];
  const seen = new Set<string>();

  for (const item of manifest.playlist.items) {
    const itemKind = (item as { itemKind?: string }).itemKind;
    // Legacy items pre-B.6c omit itemKind; treat as MEDIA.
    if (itemKind !== undefined && itemKind !== 'MEDIA') continue;
    const media = item as ManifestMediaItem;
    if (seen.has(media.sha256)) continue;
    seen.add(media.sha256);
    targets.push({
      sha256: media.sha256,
      url: media.url,
      sizeBytes: media.sizeBytes,
    });
  }

  // mediaToPrecache may be absent on legacy responses from a pre-M2E.C API;
  // treat as empty to keep the player forward-compatible.
  const precache = manifest.mediaToPrecache ?? [];
  for (const ref of precache) {
    if (seen.has(ref.sha256)) continue;
    seen.add(ref.sha256);
    targets.push({
      sha256: ref.sha256,
      url: ref.url,
      sizeBytes: ref.sizeBytes,
    });
  }

  return targets;
}

export async function hydrateManifest(
  manifest: Manifest,
  options: HydrateOptions = {},
): Promise<HydrationResult> {
  const targets = buildDownloadTargets(manifest);
  const totalItems = targets.length;

  // Diff against the cache — which targets are already on disk by sha256?
  const missing: DownloadTarget[] = [];
  let cachedBefore = 0;
  for (const t of targets) {
    if (await hasBlob(t.sha256)) {
      cachedBefore++;
    } else {
      missing.push(t);
    }
  }

  const errors: HydrationError[] = [];
  let downloaded = 0;
  let completed = cachedBefore;

  const emitProgress = (): void => {
    options.onProgress?.({
      totalItems,
      itemsCompleted: completed,
      itemsDownloaded: downloaded,
      itemsFailed: errors.length,
    });
  };
  emitProgress();

  // Worker pool with bounded concurrency.
  const queue = [...missing];
  const workers = Array.from(
    { length: Math.min(ASSET_FETCH_CONCURRENCY, queue.length) },
    async () => {
      while (queue.length > 0) {
        const target = queue.shift();
        if (!target) return;
        try {
          await downloadAndCache(target);
          downloaded++;
        } catch (err) {
          errors.push({
            sha256: target.sha256,
            message: err instanceof Error ? err.message : String(err),
          });
        } finally {
          completed++;
          emitProgress();
        }
      }
    },
  );
  await Promise.all(workers);

  const ok = errors.length === 0;
  if (ok) {
    // Promote: IDB first (data), then localStorage (pointer).
    // If anything fails between, the IDB copy is the source of truth.
    await setMeta(LAST_GOOD_KEY, manifest);
    setLastManifestHash(manifest.manifest_hash);
  }

  return {
    ok,
    totalItems,
    cachedBefore,
    downloaded,
    failed: errors.length,
    errors,
  };
}

async function downloadAndCache(target: DownloadTarget): Promise<void> {
  let res: Response;
  try {
    res = await fetch(target.url);
  } catch (err) {
    throw new Error(
      `network: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  // Size sanity check (sha256 verification is server-side; we trust it
  // and keep the client cheap for slow devices).
  const expected = parseInt(target.sizeBytes, 10);
  if (Number.isFinite(expected) && expected > 0 && blob.size !== expected) {
    throw new Error(`size mismatch (got ${blob.size}, expected ${expected})`);
  }
  await putBlob(target.sha256, blob);
}
